import { LitElement, html } from "lit";
import { query, state, property, customElement } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { live } from "lit/directives/live.js";
import { classMap } from "lit/directives/class-map.js";
import { injectStyles } from "@eloomi/design-tokens";
import { emit } from "../common/internal/event.js";
import { hasSlot } from "../common/internal/slot.js";
import styles, { globalStyles } from "./text-field.styles.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
let styleElement;
let TextField = class extends LitElement {
  constructor() {
    super(...arguments);
    this.hasFocus = false;
    this.hasIconLeftSlot = false;
    this.hasIconRightSlot = false;
    this.hasInputCloneSlot = false;
    this.inputClone = void 0;
    this.inputAttrsMutationObserver = new MutationObserver((mutationsList) => {
      if (!this.inputClone)
        return;
      for (const mutation of mutationsList) {
        if (mutation.type === "attributes") {
          const attributeName = mutation.attributeName;
          const attributeValue = this.input.getAttribute(attributeName);
          if (attributeValue === null) {
            this.inputClone.removeAttribute(attributeName);
          } else {
            this.inputClone.setAttribute(attributeName, attributeValue);
          }
        }
      }
    });
    this.type = "text";
    this.size = "large";
    this.color = "default";
    this.value = "";
    this.disabled = false;
    this.readonly = false;
    this.active = false;
    this.loading = false;
    this.required = false;
    this.autocapitalize = "";
    this.autofocus = false;
    this.spellcheck = false;
    this.invalid = false;
    this.handleBlur = () => {
      this.hasFocus = false;
      emit(this, "elm-blur");
    };
    this.handleChange = () => {
      this.value = this.getActiveInput().value;
      emit(this, "elm-change");
    };
    this.handleFocus = () => {
      this.hasFocus = true;
      emit(this, "elm-focus");
    };
    this.handleInput = () => {
      this.value = this.getActiveInput().value;
      emit(this, "elm-input");
    };
  }
  connectedCallback() {
    super.connectedCallback();
    this.handleSlotChange = this.handleSlotChange.bind(this);
    this.shadowRoot.addEventListener("slotchange", this.handleSlotChange);
    if (this.autofocus) {
      requestAnimationFrame(() => this.focus());
    }
    requestAnimationFrame(() => {
      if (this.hasInputCloneSlot) {
        this.inputAttrsMutationObserver.observe(this.input, {
          attributes: true
        });
      }
    });
    this.injectGlobalStyles();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.shadowRoot.removeEventListener("slotchange", this.handleSlotChange);
    this.inputAttrsMutationObserver.disconnect();
    if (this.inputClone) {
      this.removeEventListenersFromInput(this.inputClone);
    }
  }
  injectGlobalStyles() {
    if (styleElement && !document.head.contains(styleElement)) {
      styleElement = void 0;
    }
    if (!styleElement) {
      styleElement = injectStyles({
        content: globalStyles.toString(),
        styleElement,
        createdBy: "@eloomi/components/text-field"
      });
    }
  }
  checkIsInputCloneSlotCorrect(nodes) {
    if (nodes.length !== 1)
      return false;
    const node = nodes[0];
    return node.nodeType === Node.ELEMENT_NODE && node.tagName === "INPUT";
  }
  getSlottedInputClone() {
    var _a, _b;
    if (!this.hasInputCloneSlot)
      return;
    const slotSelector = `slot[name="input-clone"]`;
    const slot = (_a = this.renderRoot) == null ? void 0 : _a.querySelector(slotSelector);
    const nodes = (_b = slot == null ? void 0 : slot.assignedNodes({ flatten: true })) != null ? _b : [];
    if (!this.checkIsInputCloneSlotCorrect(nodes)) {
      throw new Error('Slot "input-clone" must contain single <input slot="input-clone" /> element');
    }
    return nodes[0];
  }
  getActiveInput() {
    return this.inputClone || this.input;
  }
  focus(options) {
    this.getActiveInput().focus(options);
  }
  blur() {
    this.getActiveInput().blur();
  }
  addEventListenersToInput(input) {
    input.addEventListener("input", this.handleInput);
    input.addEventListener("change", this.handleChange);
    input.addEventListener("focus", this.handleFocus);
    input.addEventListener("blur", this.handleBlur);
  }
  removeEventListenersFromInput(input) {
    input.removeEventListener("input", this.handleInput);
    input.removeEventListener("change", this.handleChange);
    input.removeEventListener("focus", this.handleFocus);
    input.removeEventListener("blur", this.handleBlur);
  }
  initInputClone() {
    const inputClone = this.getSlottedInputClone();
    if (this.inputClone && this.inputClone !== inputClone) {
      this.removeEventListenersFromInput(this.inputClone);
    }
    if (!inputClone || inputClone === this.inputClone)
      return;
    const attributes = this.input.getAttributeNames().filter((attribute) => attribute !== "style");
    for (const attribute of attributes) {
      const attributeValue = this.input.getAttribute(attribute);
      if (attributeValue !== null) {
        inputClone.setAttribute(attribute, attributeValue);
      }
    }
    this.addEventListenersToInput(inputClone);
    this.inputClone = inputClone;
    this.input.style.display = "none";
  }
  handleSlotChange() {
    this.hasIconLeftSlot = hasSlot(this, "icon-left");
    this.hasIconRightSlot = hasSlot(this, "icon-right");
    this.hasInputCloneSlot = hasSlot(this, "input-clone");
    this.initInputClone();
  }
  handleClick(event) {
    if (event.target !== this.getActiveInput()) {
      this.focus();
    }
  }
  render() {
    return html`<div part="base" class="${classMap({
      input: true,
      [`input--${this.color}`]: true,
      "input--small": this.size === "small",
      "input--large": this.size === "large",
      "input--disabled": this.disabled,
      "input--readonly": this.readonly,
      "input--active": this.active,
      "input--focused": this.hasFocus,
      "input--invalid": this.invalid,
      "input--loading": this.loading
    })}" @click="${this.handleClick}"><div class="input__skeleton"></div><span part="icon-left" class="${classMap({
      input__icon: true,
      "input__icon--left": this.hasIconLeftSlot
    })}" @click="${this.focus}"><slot name="icon-left"></slot></span><slot name="input-clone"></slot><input id="${ifDefined(this.inputId)}" part="input" class="input__control" type="${this.type}" name="${ifDefined(this.name)}" ?disabled="${this.disabled}" ?readonly="${this.readonly}" ?required="${this.required}" placeholder="${ifDefined(this.placeholder)}" minlength="${ifDefined(this.minlength)}" maxlength="${ifDefined(this.maxlength)}" min="${ifDefined(this.min)}" max="${ifDefined(this.max)}" step="${ifDefined(this.step)}" .value="${live(this.value)}" autocapitalize="${ifDefined(this.autocapitalize)}" autocomplete="${ifDefined(this.autocomplete)}" autocorrect="${ifDefined(this.autocorrect)}" ?autofocus="${this.autofocus}" spellcheck="${ifDefined(this.spellcheck)}" pattern="${ifDefined(this.pattern)}" inputmode="${ifDefined(this.inputmode)}" aria-invalid="${this.invalid ? "true" : "false"}" @change="${this.handleChange}" @input="${this.handleInput}" @focus="${this.handleFocus}" @blur="${this.handleBlur}"> <span part="icon-right" class="${classMap({
      input__icon: true,
      "input__icon--right": this.hasIconRightSlot
    })}" @click="${this.focus}"><slot name="icon-right"></slot></span></div>`;
  }
};
TextField.styles = styles;
__decorateClass([
  query(".input__control")
], TextField.prototype, "input", 2);
__decorateClass([
  state()
], TextField.prototype, "hasFocus", 2);
__decorateClass([
  state()
], TextField.prototype, "hasIconLeftSlot", 2);
__decorateClass([
  state()
], TextField.prototype, "hasIconRightSlot", 2);
__decorateClass([
  state()
], TextField.prototype, "hasInputCloneSlot", 2);
__decorateClass([
  property({ reflect: true })
], TextField.prototype, "type", 2);
__decorateClass([
  property()
], TextField.prototype, "size", 2);
__decorateClass([
  property({ reflect: true })
], TextField.prototype, "color", 2);
__decorateClass([
  property()
], TextField.prototype, "name", 2);
__decorateClass([
  property()
], TextField.prototype, "inputId", 2);
__decorateClass([
  property()
], TextField.prototype, "value", 2);
__decorateClass([
  property()
], TextField.prototype, "placeholder", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], TextField.prototype, "disabled", 2);
__decorateClass([
  property({ type: Boolean })
], TextField.prototype, "readonly", 2);
__decorateClass([
  property({ type: Boolean })
], TextField.prototype, "active", 2);
__decorateClass([
  property({ type: Boolean })
], TextField.prototype, "loading", 2);
__decorateClass([
  property({ type: Number })
], TextField.prototype, "minlength", 2);
__decorateClass([
  property({ type: Number })
], TextField.prototype, "maxlength", 2);
__decorateClass([
  property()
], TextField.prototype, "min", 2);
__decorateClass([
  property()
], TextField.prototype, "max", 2);
__decorateClass([
  property({ type: Number })
], TextField.prototype, "step", 2);
__decorateClass([
  property()
], TextField.prototype, "pattern", 2);
__decorateClass([
  property({ type: Boolean })
], TextField.prototype, "required", 2);
__decorateClass([
  property()
], TextField.prototype, "autocapitalize", 2);
__decorateClass([
  property()
], TextField.prototype, "autocorrect", 2);
__decorateClass([
  property()
], TextField.prototype, "autocomplete", 2);
__decorateClass([
  property({ type: Boolean })
], TextField.prototype, "autofocus", 2);
__decorateClass([
  property({ type: Boolean })
], TextField.prototype, "spellcheck", 2);
__decorateClass([
  property({ type: Boolean })
], TextField.prototype, "invalid", 2);
__decorateClass([
  property()
], TextField.prototype, "inputmode", 2);
TextField = __decorateClass([
  customElement("elm-text-field")
], TextField);
export { TextField as default };
